import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  UtensilsCrossed,
  Search,
  MapPin,
  Star,
  Clock,
  Leaf,
  ChevronRight,
  ShieldCheck,
  Flame,
  Truck,
  Sparkles
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { BACKEND_URL } from '../config';
import { usePageMeta } from '../hooks/usePageMeta';

const POPULAR_STATIONS = [
  { code: 'NDLS', name: 'New Delhi', state: 'Delhi' },
  { code: 'BPL', name: 'Bhopal Jn', state: 'Madhya Pradesh' },
  { code: 'HWH', name: 'Howrah Jn', state: 'West Bengal' },
  { code: 'CNB', name: 'Kanpur Central', state: 'Uttar Pradesh' },
  { code: 'NGP', name: 'Nagpur Jn', state: 'Maharashtra' },
  { code: 'MMCT', name: 'Mumbai Central', state: 'Maharashtra' },
];

const FOOD_FAQS = [
  {
    q: 'How can I order food in train online?',
    a: 'Visit the TrainPanda Food on Train page and follow the available ordering instructions. Enter the required journey details, explore available restaurants and meals, and confirm your order according to the service instructions.'
  },
  {
    q: 'Can I get train food delivery directly to my berth?',
    a: 'Food delivery to your berth may be available at supported stations through participating restaurant partners. Availability depends on your train, station, route, and order details, so confirm the delivery options before placing an order.'
  },
  {
    q: 'What types of food can I order during a train journey?',
    a: 'Available options may include breakfast, lunch, dinner, snacks, and meals from different cuisines. The menu depends on the restaurants serving your selected station and journey.'
  },
  {
    q: 'Is railway food booking available at every station?',
    a: 'No. Food delivery availability depends on the service coverage, participating restaurants, and the stations supported for your journey. Check the available options on TrainPanda before ordering.'
  },
  {
    q: 'When should I order food for my train journey?',
    a: 'It is generally helpful to check food options and place your order in advance, where the service allows it. Follow the ordering instructions and confirm the delivery station and timing to help ensure your meal is arranged for the intended stop.'
  }
];

const FoodOnTrain = () => {
  usePageMeta(
    'Order Tasty Food in Train Online - Food and Meal | TrainPanda',
    'Order fresh and tasty food on train with TrainPanda. Get meals delivered directly to your seat at railway stations across India for a convenient journey.'
  );
  const nav = useNavigate();
  const backendUrl = BACKEND_URL;
  const useVendorIframe = process.env.REACT_APP_USE_VENDOR_IFRAME === 'true';

  const [stationInput, setStationInput] = useState('');
  const [selectedStation, setSelectedStation] = useState('ALL');
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vegOnly, setVegOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async (station = '') => {
    setLoading(true);
    try {
      const url = station && station !== 'ALL'
        ? `${backendUrl}/api/restaurants?station=${station}`
        : `${backendUrl}/api/restaurants`;
      const res = await axios.get(url);
      setRestaurants(res.data || []);
    } catch (err) {
      console.error('Failed to load restaurants:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStationSearch = (e) => {
    e.preventDefault();
    const stn = stationInput.trim().toUpperCase();
    if (!stn) return;
    nav(`/food/station/${stn}`);
  };

  const handleFilterStation = (stn) => {
    setSelectedStation(stn);
    fetchRestaurants(stn === 'ALL' ? '' : stn);
  };

  const filteredRestaurants = restaurants.filter((r) => {
    const matchesVeg = !vegOnly || r.veg;
    const matchesQuery =
      !searchQuery ||
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.station.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.cuisines && r.cuisines.some((c) => c.toLowerCase().includes(searchQuery.toLowerCase())));
    return matchesVeg && matchesQuery;
  });

  if (useVendorIframe) {
    return (
      <div className="bg-gray-50">
        <div className="bg-tp-navy text-white py-3" data-testid="food-banner">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-2">
            <UtensilsCrossed size={18} className="text-tp-orange" />
            <div>
              <div className="text-sm font-semibold">Food on Train</div>
              <div className="text-xs text-white/60">Browse menus &amp; order — delivered to your seat.</div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-0 md:px-4 py-0 md:py-4">
          <iframe
            data-testid="food-iframe"
            title="Food on Train"
            src={`${backendUrl}/api/food/embed`}
            referrerPolicy="no-referrer"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals allow-downloads allow-storage-access-by-user-activation"
            allow="payment *; clipboard-write *; geolocation *"
            className="w-full bg-white border border-gray-100 md:rounded-xl card-shadow"
            style={{ height: 'calc(100vh - 220px)', minHeight: 600 }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-[#0B2E5C] via-[#103D75] to-[#E07A0F] text-white py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-amber-300 text-xs font-semibold backdrop-blur-sm mb-3">
                <Sparkles size={14} /> 500+ FSSAI Approved Restaurant Partners
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                Order Delicious Food On Train
              </h1>
              <p className="mt-2 text-sm md:text-base text-slate-200 max-w-xl">
                Get hot, hygienic meals delivered directly to your train berth at upcoming stations.
              </p>
            </div>

            {/* Station Search Card */}
            <div className="w-full md:w-96 bg-white rounded-2xl p-4 shadow-xl text-slate-800">
              <h3 className="font-bold text-sm text-[#0B2E5C] mb-2 flex items-center gap-1.5">
                <MapPin size={16} className="text-tp-orange" /> Find by Station Code / Name
              </h3>
              <form onSubmit={handleStationSearch} className="flex gap-2">
                <Input
                  value={stationInput}
                  onChange={(e) => setStationInput(e.target.value)}
                  placeholder="e.g. NDLS, BPL, HWH"
                  className="uppercase font-semibold tracking-wide h-11"
                />
                <Button type="submit" className="bg-tp-orange hover:bg-[#E07A0F] text-white font-bold h-11 px-5">
                  Search
                </Button>
              </form>
            </div>
          </div>

          {/* Quick Station Chips */}
          <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-white/80 font-semibold mr-1">Popular Stations:</span>
            <button
              onClick={() => handleFilterStation('ALL')}
              className={`text-xs px-3 py-1 rounded-full font-medium transition ${
                selectedStation === 'ALL'
                  ? 'bg-white text-[#0B2E5C] font-bold shadow'
                  : 'bg-white/15 text-white hover:bg-white/25'
              }`}
            >
              All Stations
            </button>
            {POPULAR_STATIONS.map((stn) => (
              <button
                key={stn.code}
                onClick={() => handleFilterStation(stn.code)}
                className={`text-xs px-3 py-1 rounded-full font-medium transition ${
                  selectedStation === stn.code
                    ? 'bg-amber-400 text-slate-900 font-bold shadow'
                    : 'bg-white/15 text-white hover:bg-white/25'
                }`}
              >
                {stn.name} ({stn.code})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Feature Badges */}
      <div className="max-w-6xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-green-50 text-green-600"><ShieldCheck size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-800">100% Hygienic</p>
              <p className="text-[10px] text-slate-500">FSSAI Certified Food</p>
            </div>
          </div>
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-orange-50 text-orange-600"><Truck size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-800">Seat Delivery</p>
              <p className="text-[10px] text-slate-500">Delivered right at berth</p>
            </div>
          </div>
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600"><Flame size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-800">Fresh & Hot</p>
              <p className="text-[10px] text-slate-500">Prepared fresh for trip</p>
            </div>
          </div>
          <div className="bg-white p-3 rounded-xl shadow-sm border border-slate-100 flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600"><Sparkles size={18} /></div>
            <div>
              <p className="text-xs font-bold text-slate-800">Live Train Sync</p>
              <p className="text-[10px] text-slate-500">Auto delay adjustments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-6xl mx-auto px-4 mt-4">
        {/* Controls Bar */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search dishes, cuisines, or restaurant names..."
              className="pl-9 h-10 border-slate-200"
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => setVegOnly(!vegOnly)}
              variant={vegOnly ? 'default' : 'outline'}
              className={`h-10 text-xs font-semibold ${
                vegOnly
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'border-green-500 text-green-700 hover:bg-green-50'
              }`}
            >
              <Leaf size={14} className="mr-1.5" /> Pure Veg
            </Button>
          </div>
        </div>

        {/* Section Title */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#0B2E5C]">
            {selectedStation === 'ALL'
              ? 'Available Restaurants across Stations'
              : `Restaurants at ${selectedStation}`}
          </h2>
          <span className="text-xs text-slate-500 font-medium">
            {filteredRestaurants.length} restaurants found
          </span>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm animate-pulse">
                <div className="h-44 bg-slate-200 rounded-xl mb-4" />
                <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredRestaurants.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <UtensilsCrossed size={48} className="mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-bold text-slate-700">No restaurants match your filter</h3>
            <p className="text-xs text-slate-500 mt-1">Try clearing filters or searching for another station.</p>
            <Button
              onClick={() => {
                setVegOnly(false);
                setSearchQuery('');
                handleFilterStation('ALL');
              }}
              variant="outline"
              className="mt-4"
            >
              Reset Filters
            </Button>
          </div>
        ) : (
          /* Restaurant Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredRestaurants.map((r) => (
              <div
                key={r.id}
                onClick={() => nav(`/food/restaurant/${r.id}`)}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden group flex flex-col"
              >
                {/* Image & Badges */}
                <div className="h-44 bg-slate-100 relative overflow-hidden">
                  <img
                    src={r.image}
                    alt={r.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3 flex items-center gap-1.5">
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md shadow ${
                        r.veg ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                      }`}
                    >
                      {r.veg ? 'Pure Veg' : 'Non-Veg'}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3 bg-slate-900/80 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-lg">
                    📍 {r.station}
                  </div>
                </div>

                {/* Details */}
                <div className="p-4 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-bold text-base text-[#0B2E5C] group-hover:text-tp-orange transition-colors">
                        {r.name}
                      </h3>
                      <span className="inline-flex items-center gap-1 bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                        <Star size={11} fill="white" /> {r.rating}
                      </span>
                    </div>

                    <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                      {r.cuisines?.join(' • ')}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
                    <span className="flex items-center gap-1 font-medium">
                      <Clock size={13} className="text-slate-400" /> {r.time}
                    </span>
                    <span className="font-semibold text-slate-800">
                      ₹{r.price} for two
                    </span>
                    <span className="inline-flex items-center text-tp-orange font-bold group-hover:translate-x-1 transition-transform">
                      View Menu <ChevronRight size={14} />
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* SEO & FAQ Content Section from Drive */}
        <section className="mt-16 bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-sm space-y-8">
          <div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0B2E5C] tracking-tight">
              Order Delicious Food on Train with TrainPanda
            </h3>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mt-3">
              Enjoy delicious meals during your railway journey with TrainPanda. Whether you are travelling for a few hours or taking a long-distance train, finding fresh and tasty food can make your journey more comfortable. With food in train ordering options, you can explore convenient meal delivery services and enjoy food delivered to your train seat at eligible stations.
            </p>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mt-2">
              Forget the hassle of carrying every meal from home or relying only on snacks available at railway platforms. TrainPanda helps you explore convenient train food delivery options so you can plan your meals while travelling. Whether you want breakfast, lunch, dinner, or snacks, ordering food online can make your railway journey more enjoyable.
            </p>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mt-2">
              Visit TrainPanda Food on Train to explore available food-ordering options for your next trip.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Order Food in Train Online
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Looking to order food in train without interrupting your journey? Online railway food ordering makes it easier to plan meals before hunger strikes. Instead of rushing to buy something during a short station stop, you can explore available menu options and choose food according to your preferences.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              TrainPanda helps passengers find convenient food-ordering options for their train journeys. Depending on the available service, you may be able to choose from different cuisines, meals, and snacks offered by restaurant partners at eligible railway stations.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              To get started, visit the TrainPanda Food on Train page, follow the available ordering instructions, enter the required journey details, and check the restaurants and delivery options available for your train and station.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Train Food Delivery to Your Seat
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              A comfortable journey includes convenient access to meals. With train food delivery, passengers can explore options for having food delivered directly to their train berth at supported stations.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Whether you are travelling with family, friends, or alone, advance meal planning can help you avoid last-minute confusion. You can check the available menu, compare meal choices, and select suitable options based on your journey requirements.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Delivery availability depends on the train, station, restaurant partner, order timing, and service coverage. Always confirm the delivery station and expected delivery details before placing your order.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Railway Food Booking Made Simple
            </h4>
            <p className="text-sm text-gray-600">
              Railway food booking allows passengers to explore meal options without depending entirely on food sold at railway platforms. Online ordering can be especially useful during long journeys when you want to plan your meals in advance.
            </p>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1.5 pl-1">
              <li>Visit the TrainPanda Food on Train page.</li>
              <li>Follow the available food-ordering instructions.</li>
              <li>Enter your journey or train details as requested.</li>
              <li>Explore the available restaurants and menu options.</li>
              <li>Select your meal and confirm the order details, including the delivery station and timing.</li>
            </ol>
            <p className="text-sm text-gray-600 leading-relaxed mt-2">
              Check the order information carefully to ensure that your selected service is available for your journey.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Enjoy Different Food Options During Your Journey
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Every passenger has different food preferences. Some enjoy a filling lunch, while others prefer a light breakfast, evening snacks, or a complete dinner. Exploring online food menus can help you choose meals that suit your tastes and travel schedule.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Before ordering, review the menu description, ingredients, portion details, and any available dietary information. If you have specific dietary requirements or food allergies, confirm the relevant details with the restaurant before placing your order.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Why Choose TrainPanda for Food on Train?
            </h4>
            <p className="text-sm text-gray-600">
              TrainPanda brings convenience to your railway travel planning by helping you explore food-ordering options alongside other useful travel services.
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1.5 pl-1">
              <li><strong className="text-gray-800">Convenient ordering:</strong> Explore available meals online.</li>
              <li><strong className="text-gray-800">Restaurant choices:</strong> Check food options from participating restaurant partners.</li>
              <li><strong className="text-gray-800">Seat delivery options:</strong> Look for delivery to your train berth at supported stations.</li>
              <li><strong className="text-gray-800">Journey planning:</strong> Arrange meals around your train schedule.</li>
              <li><strong className="text-gray-800">Easy access:</strong> Visit the website using your mobile phone or computer.</li>
            </ul>
            <p className="text-sm text-gray-600 leading-relaxed mt-2">
              Availability, menus, prices, and delivery times may vary by route and station. Check the latest details before confirming your order.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Make Your Train Journey More Delicious
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Good food can make a long railway journey more enjoyable. Whether you want to order food in train for lunch, dinner, or snacks, TrainPanda helps you explore convenient meal options for your trip.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Plan ahead, check the available restaurants, and confirm your delivery details to make your journey smoother.
            </p>
            <p className="text-sm font-semibold text-tp-orange">
              Order food on train with TrainPanda: <a href="https://www.trainpanda.com/food-on-train" className="underline hover:text-tp-orange/80">https://www.trainpanda.com/food-on-train</a>
            </p>
          </div>

          {/* FAQ Section */}
          <div className="pt-6 border-t border-gray-200">
            <h4 className="text-xl font-bold text-[#0B2E5C] mb-4">
              Frequently Asked Questions (FAQs)
            </h4>
            <div className="space-y-3">
              {FOOD_FAQS.map((faq, idx) => (
                <div key={idx} className="border border-gray-200/80 rounded-2xl overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-5 py-4 text-left font-bold text-gray-800 flex items-center justify-between gap-4 hover:text-tp-orange transition-colors"
                  >
                    <span>{idx + 1}. {faq.q}</span>
                    <span className="text-xl text-gray-400 font-light select-none">
                      {openFaq === idx ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FoodOnTrain;
